import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Filter,
  Plus,
  RotateCcw,
  Search,
  UserRound,
  Wrench,
} from "lucide-react";
import { TicketTable } from "../components/TicketTable";
import {
  Button,
  ButtonLink,
  Card,
  Input,
  Label,
  PageHeader,
  Select,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { defaultTicketPeriod } from "../lib/format";

const DEFAULT_PERIOD = defaultTicketPeriod();

export function TicketsPage() {
  const { session } = useAuth();
  const isMira = session?.codigoEmpresa === 52;
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("TODOS");
  const [startDate, setStartDate] = useState(DEFAULT_PERIOD.startDate);
  const [endDate, setEndDate] = useState(DEFAULT_PERIOD.endDate);
  const [companyId, setCompanyId] = useState("0");
  const [ticketType, setTicketType] = useState("Todos");
  const [assigneeId, setAssigneeId] = useState("");
  const [page, setPage] = useState(1);

  const companies = useQuery({
    queryKey: ["companies", "ticket-filters"],
    queryFn: api.getCompanies,
    enabled: isMira,
  });

  const supportUsers = useQuery({
    queryKey: ["support-users", 52],
    queryFn: api.getSupportUsers,
    enabled: isMira,
  });

  const effectiveCompanyId = isMira
    ? Number(companyId)
    : (session?.codigoEmpresa ?? 0);

  const tickets = useQuery({
    queryKey: [
      "tickets",
      effectiveCompanyId,
      page,
      search,
      priority,
      status,
      startDate,
      endDate,
      ticketType,
      assigneeId,
    ],
    queryFn: () =>
      api.listTickets(effectiveCompanyId, {
        page,
        pageSize: 10,
        query: search,
        priority,
        status,
        startDate,
        endDate,
        type: isMira ? ticketType : "Todos",
        assigneeId: isMira && assigneeId ? Number(assigneeId) : undefined,
      }),
    enabled: Boolean(session),
  });

  const pageCount = Math.max(
    1,
    Math.ceil((tickets.data?.totalDeItens ?? 0) / (tickets.data?.tamanhoPagina || 10)),
  );

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setSearch(draftSearch.trim());
  }

  function resetFilters() {
    setDraftSearch("");
    setSearch("");
    setPriority("");
    setStatus("TODOS");
    setStartDate(DEFAULT_PERIOD.startDate);
    setEndDate(DEFAULT_PERIOD.endDate);
    setCompanyId("0");
    setTicketType("Todos");
    setAssigneeId("");
    setPage(1);
  }

  function updateFilter(setter: (value: string) => void, value: string) {
    setter(value);
    setPage(1);
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Atendimento"
        title="Chamados"
        description={
          isMira
            ? "Gerencie a fila de atendimento de todas as empresas."
            : "Consulte o histórico, filtre solicitações e acompanhe cada atualização."
        }
        actions={
          <ButtonLink to="/chamados/novo">
            <Plus className="size-4" />
            Novo chamado
          </ButtonLink>
        }
      />

      <Card className="filter-panel">
        <form onSubmit={submitSearch} className="filter-search">
          <Search />
          <Input
            id="ticket-search"
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Número do chamado, assunto ou descrição..."
            aria-label="Pesquisar chamados"
          />
          <Button type="submit" variant="secondary" size="sm">Pesquisar</Button>
        </form>

        {isMira && (
          <section className="staff-filter-section">
            <header>
              <span><Wrench /></span>
              <div>
                <strong>Filtros da equipe Mira</strong>
                <small>Refine a fila por cliente, responsável e tipo de atendimento.</small>
              </div>
            </header>
            <div className="staff-filter-grid">
              <div>
                <Label htmlFor="filter-company"><Building2 /> Empresa</Label>
                <Select
                  id="filter-company"
                  value={companyId}
                  onChange={(event) => updateFilter(setCompanyId, event.target.value)}
                  disabled={companies.isLoading}
                >
                  <option value="0">Todas as empresas</option>
                  {companies.data?.map((company) => (
                    <option key={company.id} value={company.id}>{company.empresa}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-assignee"><UserRound /> Responsável Mira</Label>
                <Select
                  id="filter-assignee"
                  value={assigneeId}
                  onChange={(event) => updateFilter(setAssigneeId, event.target.value)}
                  disabled={supportUsers.isLoading}
                >
                  <option value="">Todos os responsáveis</option>
                  {supportUsers.data?.map((user) => (
                    <option key={user.codigo} value={user.codigo}>{user.usuario}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-type"><Wrench /> Tipo</Label>
                <Select
                  id="filter-type"
                  value={ticketType}
                  onChange={(event) => updateFilter(setTicketType, event.target.value)}
                >
                  <option value="Todos">Todos os tipos</option>
                  <option value="Suporte">Suporte</option>
                  <option value="Desenvolvimento">Desenvolvimento</option>
                </Select>
              </div>
            </div>
          </section>
        )}

        <div className="filter-divider" />
        <div className="filter-controls">
          <span className="filter-label"><Filter /> Filtros</span>
          <Select
            value={priority}
            onChange={(event) => updateFilter(setPriority, event.target.value)}
            aria-label="Filtrar por prioridade"
          >
            <option value="">Todas as prioridades</option>
            <option value="Baixa">Prioridade baixa</option>
            <option value="Média">Prioridade média</option>
            <option value="Alta">Prioridade alta</option>
            <option value="Altíssima">Prioridade altíssima</option>
          </Select>
          <Select
            value={status}
            onChange={(event) => updateFilter(setStatus, event.target.value)}
            aria-label="Filtrar por status"
          >
            <option value="TODOS">Todos os status</option>
            <option value="ABERTOS">Em aberto</option>
            <option value="FINALIZADOS">Finalizados</option>
          </Select>
          <label className="compact-date-field">
            <CalendarRange />
            <span>De</span>
            <Input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(event) => updateFilter(setStartDate, event.target.value)}
              aria-label="Data inicial"
            />
          </label>
          <label className="compact-date-field">
            <span>Até</span>
            <Input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(event) => updateFilter(setEndDate, event.target.value)}
              aria-label="Data final"
            />
          </label>
          <button className="reset-filter" type="button" onClick={resetFilters}>
            <RotateCcw /> Limpar
          </button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="section-heading">
          <div>
            <h2>Solicitações</h2>
            <p>
              {tickets.isLoading
                ? "Carregando chamados..."
                : `${tickets.data?.totalDeItens ?? 0} chamado(s) encontrado(s)`}
            </p>
          </div>
          {tickets.isFetching && !tickets.isLoading && (
            <span className="refreshing-label">Atualizando...</span>
          )}
        </div>
        {tickets.isError ? (
          <div className="inline-error">
            Ocorreu um erro ao consultar os chamados.
            <button onClick={() => tickets.refetch()}>Tentar novamente</button>
          </div>
        ) : (
          <TicketTable tickets={tickets.data?.itens} loading={tickets.isLoading} />
        )}
        <footer className="pagination">
          <span>
            Página <strong>{page}</strong> de <strong>{pageCount}</strong>
          </span>
          <div>
            <Button
              variant="secondary"
              size="icon"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              aria-label="Página anterior"
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              disabled={page >= pageCount}
              onClick={() => setPage((current) => current + 1)}
              aria-label="Próxima página"
            >
              <ChevronRight />
            </Button>
          </div>
        </footer>
      </Card>
    </div>
  );
}
